import { useEffect, type ReactNode } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './auth/AuthContext'
import { useLatestBuild } from './hooks/useLatestBuild'
import { Login } from './auth/Login'
import { FlowerMark } from './components/FlowerMark'
import { shop } from './lib/shop'
import {
  CustomersMark,
  MoreMark,
  ReportsMark,
  VendorsMark,
} from './components/SectionMarks'

import { Account } from './pages/Account'
import { NewSale } from './pages/NewSale'
import { Payments } from './pages/Payments'
import { CustomerLedgerPage } from './pages/CustomerLedgerPage'
import { Balances } from './pages/Balances'
import { Customers } from './pages/Customers'
import { Items } from './pages/Items'
import { OpeningBalances } from './pages/OpeningBalances'

import { SupplyEntry } from './pages/SupplyEntry'
import { SupplyRates } from './pages/SupplyRates'
import { DailyCash } from './pages/DailyCash'
import { Advances } from './pages/Advances'
import { Settlement } from './pages/Settlement'
import { DayBookPage } from './pages/DayBookPage'
import { Vendors } from './pages/Vendors'

import { Earnings } from './pages/Earnings'
import { Tools } from './pages/Tools'
import { Rates } from './pages/Rates'
import { SalesReport } from './pages/SalesReport'
import { FlowersInOut } from './pages/FlowersInOut'
import { Dashboard } from './pages/Dashboard'

/**
 * A tab, and who it is for.
 *
 * <p>{@code ownerOnly} is not the lock. The server refuses these routes to a
 * counter account whatever the browser does, and that is what actually protects
 * the book. This flag is about the screen: a button that answers "you may not"
 * is worse than no button, because it invites the question every day and teaches
 * the person using it that the app is broken.
 *
 * <p>So the two live side by side on purpose. The server decides; this decides
 * what is worth showing.
 */
interface Tab {
  to: string
  label: string
  ownerOnly?: boolean
}

const CUSTOMER_TABS: Tab[] = [
  { to: '/customers/new-sale', label: 'نیا سودا' },
  { to: '/customers/payment', label: 'ادائیگی' },
  { to: '/customers/ledger', label: 'کھاتہ', ownerOnly: true },
  { to: '/customers/balances', label: 'بقایا', ownerOnly: true },
  // Names stay: a buyer nobody has written down before still has to be written
  // down before the sale can be. Removing a name is owner-only inside the page.
  { to: '/customers/names', label: 'گاہک' },
  { to: '/customers/items', label: 'چیزیں', ownerOnly: true },
  { to: '/customers/opening', label: 'پرانا حساب', ownerOnly: true },
]

const VENDOR_TABS: Tab[] = [
  { to: '/vendors/supply', label: 'سپلائی' },
  { to: '/vendors/lots', label: 'ریٹ لگائیں', ownerOnly: true },
  { to: '/vendors/day', label: 'خرچہ اور روزانہ حساب', ownerOnly: true },
  { to: '/vendors/advances', label: 'ایڈوانس اور ادائیگی', ownerOnly: true },
  { to: '/vendors/settlement', label: 'زمیندار کھاتہ', ownerOnly: true },
  { to: '/vendors/daybook', label: 'روزنامچہ', ownerOnly: true },
  { to: '/vendors/names', label: 'زمیندار', ownerOnly: true },
]

const REPORT_TABS: Tab[] = [
  { to: '/reports/business', label: 'کاروبار', ownerOnly: true },
  { to: '/reports/sales', label: 'بکری کی رپورٹ', ownerOnly: true },
  { to: '/reports/flowers', label: 'آیا اور بکا', ownerOnly: true },
  { to: '/reports/earnings', label: 'مہینے کا حساب', ownerOnly: true },
  { to: '/reports/rates', label: 'ریٹ کا ریکارڈ', ownerOnly: true },
]

const MORE_TABS: Tab[] = [{ to: '/more', label: 'بیک اپ اور بن', ownerOnly: true }]

const visibleTabs = (tabs: Tab[], isOwner: boolean): Tab[] =>
  isOwner ? tabs : tabs.filter((tab) => !tab.ownerOnly)

/**
 * Sends a counter account back to its own screen rather than drawing a page it
 * may not have.
 *
 * <p>The tab is already hidden, so nobody arrives here by tapping. This is for
 * the address bar, a bookmark, or a link someone was sent - and for the plain
 * fact that a page which loads and then fills with refusals looks like a fault
 * in the app rather than a rule in it.
 *
 * <p>It is a courtesy, not a defence. Everything worth protecting behind these
 * routes is refused by the server to a STAFF token, and would be refused just
 * the same if this component did not exist.
 */
function OwnerOnly({ children }: { children: ReactNode }) {
  const { isOwner } = useAuth()
  return isOwner ? <>{children}</> : <Navigate to="/customers/new-sale" replace />
}

export function App() {
  const { user, ready, isOwner } = useAuth()
  const location = useLocation()

  // Puts the tab on the newest build the next time he leaves a screen, without
  // showing him anything. See useLatestBuild.
  useLatestBuild()

  /* «میرا کھاتہ» belongs to neither book. Without a section of its own it fell
     through to the customer one and wore that section's tab bar and colour,
     which read as though the page were part of گاہک. */
  const section = location.pathname.startsWith('/account')
    ? 'account'
    : location.pathname.startsWith('/vendors')
    ? 'vendor'
    : location.pathname.startsWith('/reports')
      ? 'report'
      : location.pathname.startsWith('/more')
        ? 'more'
        : 'customer'

  // The whole page changes temperature with the book you are in, so a glance at
  // the screen from across the shop tells you which side you are working on.
  useEffect(() => {
    document.documentElement.setAttribute('data-section', section)
  }, [section])

  if (!ready) {
    return <div className="login"><p className="empty">کھل رہا ہے…</p></div>
  }

  if (!user) return <Login />

  const tabs = visibleTabs(
    section === 'vendor'
      ? VENDOR_TABS
      : section === 'report'
        ? REPORT_TABS
        : section === 'more'
          ? MORE_TABS
          : section === 'account'
            ? []
            : CUSTOMER_TABS,
    isOwner,
  )

  return (
    <div className="app">
      <TopBar />
      {/* A section with no tabs draws no bar at all - an empty strip with a
          border under it looks like tabs that failed to load. */}
      {tabs.length > 0 && (
      <nav className="tabs no-print">
        <div className="tabs__inner">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) => (isActive ? 'is-active' : '')}
            >
              {tab.label}
            </NavLink>
          ))}
        </div>
      </nav>
      )}

      <main>
        <Routes>
          <Route path="/" element={<Navigate to="/customers/new-sale" replace />} />

          <Route path="/customers/new-sale" element={<NewSale />} />
          <Route path="/customers/payment" element={<Payments />} />
          <Route path="/customers/ledger" element={<OwnerOnly><CustomerLedgerPage /></OwnerOnly>} />
          <Route path="/customers/balances" element={<OwnerOnly><Balances /></OwnerOnly>} />
          <Route path="/customers/names" element={<Customers />} />
          <Route path="/customers/items" element={<OwnerOnly><Items /></OwnerOnly>} />
          <Route path="/customers/opening" element={<OwnerOnly><OpeningBalances /></OwnerOnly>} />

          <Route path="/vendors/supply" element={<SupplyEntry />} />
          <Route path="/vendors/lots" element={<OwnerOnly><SupplyRates /></OwnerOnly>} />
          <Route path="/vendors/day" element={<OwnerOnly><DailyCash /></OwnerOnly>} />
          <Route path="/vendors/advances" element={<OwnerOnly><Advances /></OwnerOnly>} />
          <Route path="/vendors/settlement" element={<OwnerOnly><Settlement /></OwnerOnly>} />
          <Route path="/vendors/daybook" element={<OwnerOnly><DayBookPage /></OwnerOnly>} />
          <Route path="/vendors/names" element={<OwnerOnly><Vendors /></OwnerOnly>} />

          <Route path="/reports/business" element={<OwnerOnly><Dashboard /></OwnerOnly>} />
          <Route path="/reports/sales" element={<OwnerOnly><SalesReport /></OwnerOnly>} />
          <Route path="/reports/flowers" element={<OwnerOnly><FlowersInOut /></OwnerOnly>} />
          <Route path="/reports/earnings" element={<OwnerOnly><Earnings /></OwnerOnly>} />
          <Route path="/reports/rates" element={<OwnerOnly><Rates /></OwnerOnly>} />

          <Route path="/more" element={<OwnerOnly><Tools /></OwnerOnly>} />
          {/* Not owner-only on purpose: the counter account has no «مزید», and it
              is exactly the one whose PIN gets changed. */}
          <Route path="/account" element={<Account />} />

          <Route path="*" element={<Navigate to="/customers/new-sale" replace />} />
        </Routes>
      </main>
    </div>
  )
}

/**
 * The four books.
 *
 * Each carries a short label as well as a long one. On a phone these four sit
 * in a bar across the bottom of the screen, a quarter of the width each, and
 * "گاہک — اُدھار" does not fit in 90 pixels. The long form comes back on a wider
 * screen, where there is room for it to say what it means.
 */
const SECTIONS = [
  {
    prefix: '/customers',
    to: '/customers/new-sale',
    label: 'گاہک — اُدھار',
    short: 'گاہک',
    Mark: CustomersMark,
  },
  { prefix: '/vendors', to: '/vendors/supply', label: 'زمیندار', short: 'زمیندار', Mark: VendorsMark },
  {
    prefix: '/reports',
    to: '/reports/sales',
    label: 'رپورٹ',
    short: 'رپورٹ',
    ownerOnly: true,
    Mark: ReportsMark,
  },
  { prefix: '/more', to: '/more', label: 'مزید', short: 'مزید', ownerOnly: true, Mark: MoreMark },
]

function TopBar() {
  const { user, signOut, isOwner } = useAuth()
  const location = useLocation()

  return (
    <header className="topbar no-print">
      <div className="topbar__inner">
        <NavLink to="/customers/new-sale" className="brand">
          {/* The topbar is white and gold needs a dark ground, so the mark gets
              its own small plate rather than fading into the header. */}
          <span className="brand__tile">
            <FlowerMark size={22} />
          </span>
          {shop.name}
        </NavLink>

        {/*
          The section pill stays lit for every tab inside that section, so it is
          always clear which of the two books is open - NavLink on its own would
          only light up on one exact tab.
        */}
        <nav className="sections">
          {SECTIONS.filter((item) => isOwner || !item.ownerOnly).map((item) => (
            <NavLink
              key={item.prefix}
              to={item.to}
              className={location.pathname.startsWith(item.prefix) ? 'is-active' : ''}
            >
              <item.Mark />
              <span className="sections__long">{item.label}</span>
              <span className="sections__short">{item.short}</span>
            </NavLink>
          ))}
        </nav>

        {/* The name is the way in to changing a PIN. It is the one place every
            account can reach, owner or counter. */}
        <NavLink to="/account" className="who">
          {user?.displayName}
        </NavLink>
        <button type="button" className="btn btn--ghost btn--small" onClick={signOut}>
          بند کریں
        </button>
      </div>
    </header>
  )
}
